import React, { useCallback, useEffect, useRef, useState } from "react"
import { createWidget } from "@typeform/embed"
import "@typeform/embed/build/css/widget.css"

import { quoteHiddenFields } from "../quoteLink"
import { REVIEWS } from "../data/reviews"
const FORM_ID = "gUb3QowC"
const MEAL_PREP_URL = "https://castironkitchen.bottle.com/s/1721"
const FAMILY_PANS_URL = "https://castironkitchen.bottle.com/s/2230"

// Scroll so the form sits just below the pinned header, then correct once if the page moved meanwhile.
const scrollToForm = (e) => {
  const book = document.getElementById("book")
  if (!book) return
  e.preventDefault()
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const target = () => {
    const header = document.querySelector("header")
    return book.getBoundingClientRect().top + window.scrollY - (header ? header.offsetHeight : 0) - 8
  }
  window.scrollTo({ top: target(), behavior: reduceMotion ? "auto" : "smooth" })
  window.setTimeout(() => {
    if (Math.abs(book.getBoundingClientRect().top - (document.querySelector("header")?.offsetHeight || 0) - 8) > 4) {
      window.scrollTo({ top: target(), behavior: "auto" })
    }
  }, reduceMotion ? 50 : 900)
  window.history.replaceState(null, "", "#book")
}

// Sun-dried tomato is reserved for this one action, so the eye always knows what to click.
// Every button scrolls to the form on the page; `place` tells PostHog which button was clicked.
const Cta = ({ className = "", place, children = "Get a Catering Quote" }) => (
  <a
    href="#book"
    onClick={scrollToForm}
    data-ph-capture-attribute-cta={place}
    className={`inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white rounded-full bg-tomato hover:bg-[#8d2f1e] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tomato ${className}`}
  >
    {children}
  </a>
)

const Stars = ({ className }) => (
  <span className="flex gap-0.5" aria-label="Five out of five stars">
    {[...Array(5)].map((_, i) => (
      <svg key={i} className={className} fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </span>
)

const Check = ({ className }) => (
  <svg className={`flex-shrink-0 w-5 h-5 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
  </svg>
)

// One thin red bar that is the button: the whole bar is clickable and it stays pinned to the top.
// The logo sits large beside "Why we're different" and in the footer.
const Nav = () => (
  <>
    <header className="sticky top-0 z-30 shadow-md">
      <a
        href="#book"
        onClick={scrollToForm}
        data-ph-capture-attribute-cta="header"
        className="flex items-center justify-center w-full gap-2 px-4 py-3 text-base font-bold text-white transition-colors duration-200 bg-tomato hover:bg-[#8d2f1e] md:text-lg md:py-3.5 group focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white"
      >
        Get a Catering Quote
        <svg className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5-5 5M5 12h13" />
        </svg>
      </a>
    </header>
  </>
)

const Hero = () => (
  <section className="relative flex items-end w-full overflow-hidden min-h-[52vh] md:min-h-[420px] md:h-[min(50vw,86vh)]">
    <img
      src="https://ik.imagekit.io/iochxp50q/cik/5.jpeg?updatedAt=1767358208893&tr=w-2400,q-85"
      srcSet="https://ik.imagekit.io/iochxp50q/cik/5.jpeg?updatedAt=1767358208893&tr=w-1200,q-85 1200w, https://ik.imagekit.io/iochxp50q/cik/5.jpeg?updatedAt=1767358208893&tr=w-2400,q-85 2400w, https://ik.imagekit.io/iochxp50q/cik/5.jpeg?updatedAt=1767358208893&tr=w-3024,q-85 3024w"
      sizes="(min-width: 768px) 100vw, 190vw"
      alt="Cast Iron Kitchen catering trays of pasta, chicken and roasted potatoes set up at an event"
      className="absolute inset-0 object-cover w-full h-full object-[50%_88%] scale-125 origin-bottom md:object-[50%_100%] md:scale-100"
    />
    <div className="absolute inset-x-0 bottom-0 h-3/4 md:h-1/2 bg-gradient-to-t from-black/90 via-black/60 to-transparent md:from-black/85 md:via-black/50" />

    <div className="relative z-10 w-full px-5 pb-5 mx-auto max-w-6xl md:pb-8">
      <p className="mb-2 text-sm font-semibold text-white md:text-base xl:text-lg [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]">
        Sicilian &amp; Italian catering in Willowick, Ohio
      </p>
      <h1 className="[text-shadow:0_2px_12px_rgba(0,0,0,0.5)] max-w-2xl text-balance text-3xl font-extrabold leading-[1.05] tracking-tight text-white font-display md:text-5xl xl:text-6xl">
        Food your guests will talk about for years
      </h1>

      <div className="mt-4">
        <Cta place="hero" className="w-full sm:w-auto" />
      </div>

      <div className="flex flex-wrap items-center mt-3 text-sm gap-x-6 gap-y-2 md:text-base">
        <a href="#reviews" className="flex items-center gap-2.5 group">
          <Stars className="w-4 h-4 text-white" />
          <span className="font-bold text-white underline underline-offset-4 decoration-white/40 group-hover:decoration-white">
            100+ five-star reviews
          </span>
        </a>
        <span className="font-bold text-white">As seen on FOX 8</span>
      </div>
    </div>
  </section>
)

// Menu pages, shown as images: the food page first, then how we serve it.
const MENU_PAGES = [
  {
    src: "/images/menu-food.jpg",
    srcSet: "/images/menu-food-1200.jpg 1200w, /images/menu-food.jpg 2400w",
    width: 2400,
    height: 3105,
    alt: "Cast Iron Kitchen catering menu: entrees, sides, salad, bread and sauces",
  },
  {
    src: "/images/menu-three-ways.jpg",
    srcSet: "/images/menu-three-ways-1200.jpg 1200w, /images/menu-three-ways.jpg 2400w",
    width: 2400,
    height: 3105,
    alt: "Three ways we do it: Family Style, Served Buffet and Hot Drop-Off, and what you get with each",
  },
]

// Full-screen view of one menu page. Tap the page to zoom, tap outside, press X or Esc to close.
const Lightbox = ({ page, onClose }) => {
  const closeRef = useRef(null)
  const [zoomed, setZoomed] = useState(false)

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
    }
    closeRef.current?.focus()
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  return (
    <div role="dialog" aria-modal="true" aria-label={page.alt} className="fixed inset-0 z-50 bg-black/90" onClick={onClose}>
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close menu"
        className="fixed z-10 flex items-center justify-center w-12 h-12 rounded-full top-4 right-4 bg-linen text-castiron hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div className={`w-full h-full overflow-auto ${zoomed ? "" : "flex items-center justify-center p-4 md:p-8"}`}>
        <img
          src={page.src}
          alt={page.alt}
          onClick={(e) => {
            e.stopPropagation()
            setZoomed((z) => !z)
          }}
          className={
            zoomed
              ? "block w-[220%] max-w-[1600px] mx-auto cursor-zoom-out"
              : "block max-w-full max-h-full object-contain cursor-zoom-in"
          }
        />
      </div>

      <p className="fixed inset-x-0 text-sm text-center pointer-events-none bottom-3 text-linen/70">
        {zoomed ? "Tap the menu to zoom out" : "Tap the menu to zoom in"}
      </p>
    </div>
  )
}

const Menu = () => {
  const [openPage, setOpenPage] = useState(null)
  const openerRef = useRef(null)
  const close = useCallback(() => {
    setOpenPage(null)
    openerRef.current?.focus()
  }, [])

  return (
    <section id="menu" className="px-4 pt-2 pb-10 scroll-mt-20 bg-linen sm:px-5 md:pt-4 md:pb-14">
      <div className="mx-auto max-w-7xl">
        <h2 className="sr-only">The Menu</h2>

        <div className={`grid gap-4 md:gap-6 ${MENU_PAGES.length > 1 ? "md:grid-cols-2" : "max-w-xl mx-auto"}`}>
          {MENU_PAGES.map((page, i) => (
            <button
              key={page.src}
              type="button"
              onClick={(e) => {
                openerRef.current = e.currentTarget
                setOpenPage(i)
              }}
              aria-label={`Open full screen: ${page.alt}`}
              className="block overflow-hidden border rounded-lg border-sage cursor-zoom-in focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rosemary"
            >
              <img
                src={page.src}
                srcSet={page.srcSet}
                sizes={MENU_PAGES.length > 1 ? "(min-width: 768px) 50vw, 100vw" : "(min-width: 640px) 576px, 100vw"}
                alt={page.alt}
                width={page.width}
                height={page.height}
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
              />
            </button>
          ))}
        </div>

        {/* The menu as text, for Google and screen readers. */}
        <div className="sr-only">
          <h3>Cast Iron Kitchen catering menu</h3>
          <p>Cucina di Famigghia. Best Caterer, Lake County 2026.</p>
          <h4>Entrees, pick 2</h4>
          <ul>
            <li>Chicken Cutlet (Cutuletta): crisp and golden. No gluten ingredients, milk.</li>
            <li>Greek Chicken (Pullu â Greca): lemon and oregano. No gluten ingredients, milk, mustard.</li>
            <li>Meatballs (Purpetti): pecorino and herbs. No gluten ingredients, milk, egg.</li>
            <li>Chicken alla Parmigiana (Parmiciana): marinara and pecorino. No gluten ingredients, milk. Premium.</li>
            <li>Roasted Lamb (Agneddu Arrustutu): garlic and oregano. No gluten ingredients, milk, mustard. Premium.</li>
            <li>Eggplant Parmigiana (Parmiciana di Mulinciani): marinara and pecorino. No gluten ingredients, milk.</li>
          </ul>
          <h4>Sides, pick 2</h4>
          <ul>
            <li>Pasta Marinara (Pasta cu Pumaroru): slow-simmered tomato. Wheat.</li>
            <li>Garlic &amp; Oil Pasta (Agghiu e Ogghiu): garlic, extra virgin olive oil and pecorino. Milk, wheat.</li>
            <li>Garlic Roasted Potatoes (Patati Arrustuti): crisp and golden. No gluten ingredients.</li>
            <li>Charred Green Beans (Fasulina): blistered with garlic. No gluten ingredients.</li>
            <li>Garlic &amp; Chive Mashed (Patati Scacciati): olive oil and chive. No gluten ingredients. Premium.</li>
            <li>Seasonal Roasted Vegetables (Virdura di Staciuni). No gluten ingredients. Premium.</li>
          </ul>
          <h4>Salad, pick 1</h4>
          <ul>
            <li>House Salad (&apos;Nsalata di Casa): greens and vinaigrette. No gluten ingredients, mustard.</li>
            <li>Tomato Salad (&apos;Nsalata di Pumaroru): cucumber and red onion. No gluten ingredients. Premium.</li>
            <li>Arugula Salad (&apos;Nsalata di Ruca): pecorino and lemon. No gluten ingredients, milk. Premium.</li>
          </ul>
          <h4>Bread, included</h4>
          <ul>
            <li>House Ciabatta (Pani di Casa): crackly crust and sea salt. Wheat.</li>
          </ul>
          <h4>Sauces, included</h4>
          <ul>
            <li>Garlic &amp; Herb: creamy garlic and oregano. No gluten ingredients, milk, mustard.</li>
            <li>Sicilian Pesto: sweet tomato, basil and pecorino. No gluten ingredients, milk, tree nut.</li>
            <li>Honey Lemon: bright honey and lemon. No gluten ingredients, milk, mustard.</li>
          </ul>
          <p>Premium dishes are for Served Buffet and Family Style. Extra virgin olive oil only. Nothing from a can but the tomatoes.</p>

          <h3>Three ways we do it</h3>
          <ul>
            <li>Family Style, from 100 guests. Best for weddings and milestones. Full service, no lines.</li>
            <li>Served Buffet, our most popular, from 50 guests. Best for weddings, rehearsal dinners, showers, grad parties, company events and celebrations of life. Be a guest at your own party.</li>
            <li>Hot Drop-Off, from 25 guests. Best for office lunches, casual parties, picnics, birthdays and family gatherings. Hot, easy and self-serve.</li>
          </ul>
          <p>All three: a full dinner made from scratch, delivered hot, set up by us, with nothing to return.</p>
          <p>
            Family Style and Served Buffet also include premium dishes, stainless chafers and serving ware, servers on
            the buffet, food kept hot and full, leftovers packed in your fridge, and we clean up.
          </p>
          <p>
            Family Style also includes European-style courses at the table, double the staff, porcelain bowls and
            serving ware, porcelain plates and silverware, and one point person.
          </p>
        </div>

        <div className="flex flex-col items-center gap-4 mt-8">
          <Cta place="menu" className="w-full sm:w-auto" />
        </div>
      </div>

      {openPage !== null && <Lightbox page={MENU_PAGES[openPage]} onClose={close} />}
    </section>
  )
}

// Carousel: our own event photos. Captions give the event type, guest count and service style only (names stay in the reviews).
// Hot buffets, family style and drop-off pans first; one charcuterie and one appetizer shot only.
const IK = "https://ik.imagekit.io/iochxp50q/cik"
const IMG = "/images"
const CAROUSEL = [
  { src: `${IMG}/marissa-wedding-table.jpg`, event: "Wedding", detail: "150 guests", alt: "Guests' plates of chicken cutlet, meatballs, pasta and potatoes at a candlelit wedding reception" },
  { src: `${IK}/6.jpeg?tr=w-800,q-80`, event: "Celebration of life", detail: "120 guests · Buffet", alt: "Buffet line of chicken parmigiana, meatballs and baked pasta" },
  { src: `${IMG}/steak-lemon-chicken-rigatoni.jpg`, event: "Buffet", alt: "Chafers of sliced steak, lemon chicken and vodka rigatoni" },
  { src: `${IK}/2.jpeg?tr=w-800,q-80`, event: "Corporate catering", alt: "Trays of chicken cutlets and pasta for a company meal" },
  { src: `${IMG}/baked-penne-meatballs.jpg`, event: "Buffet", alt: "Chafers of baked penne, meatballs in sauce and green beans" },
  { src: `${IK}/7.jpeg?tr=w-800,q-80`, event: "Wedding", detail: "150 guests · Charcuterie", alt: "Long grazing table at an outdoor wedding" },
  { src: `${IMG}/penne-chicken-parm-buffet.jpg`, event: "Buffet", alt: "Buffet of chicken parmigiana and penne marinara" },
  { src: `${IMG}/mediterranean-spread.jpg`, event: "Mediterranean buffet", alt: "Lemon feta orzo, Greek salad, steak tips, roasted potatoes, green beans and blackened chicken", wide: true },
  { src: `${IMG}/lake-dinner-serving.jpg`, event: "Lakeside dinner party", detail: "Family style", alt: "Serving a platter to guests at a lakefront dinner party" },
  { src: `${IMG}/cutlet-buffet-line.jpg`, event: "Buffet", alt: "Buffet line of chicken cutlets, roasted potatoes, roasted vegetables and salad in chafers" },
  { src: `${IMG}/lemon-chicken-penne.jpg`, event: "Buffet", alt: "Pans of lemon chicken with parmesan, penne and greens" },
  { src: `${IMG}/hot-buffet-chafers.jpg`, event: "Buffet", alt: "Hot buffet of chicken, pasta and green beans in chafers", wide: true },
  { src: `${IK}/1.jpeg?tr=w-800,q-80`, event: "Church dinner", detail: "Buffet", alt: "Cutlets, roasted potatoes, salad, house bread and sauce cups at a church dinner" },
  { src: `${IMG}/lake-dinner-salads.jpg`, event: "Lakeside dinner party", detail: "Family style", alt: "Bowls of Greek salad with feta ready to serve" },
  { src: `${IMG}/cutlets-mash-green-beans.jpg`, event: "Buffet", alt: "Chafing dishes of chicken cutlets, garlic mashed potatoes and green beans" },
  { src: `${IK}/5.jpeg?updatedAt=1767358208893&tr=w-800,q-80`, event: "Golf outing fundraiser", detail: "Buffet", alt: "Buffet of pasta, meatballs and chicken at a golf outing fundraiser" },
  { src: `${IMG}/bruschetta-caprese-skewers.jpg`, event: "Appetizers", alt: "Trays of tomato bruschetta and salami caprese skewers" },
]

const REVIEW_PREVIEW = 8 // two rows of 4 on a laptop; phones show the first 4

// One review. Long ones are cut to 4 lines on phones, 6 on bigger screens, with "Read more" so the grid stays tidy.
const ReviewCard = ({ review, hideOnPhone }) => {
  const textRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [clamped, setClamped] = useState(false)
  useEffect(() => {
    const el = textRef.current
    if (!el) return
    const check = () => setClamped(el.scrollHeight > el.clientHeight + 1)
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  return (
    <li className={`flex flex-col p-4 bg-white rounded-2xl sm:p-5 ${hideOnPhone ? "hidden sm:flex" : ""}`}>
      <Stars className="w-3.5 h-3.5 text-castiron" />
      <p ref={textRef} className={`mt-2 text-sm leading-relaxed text-castiron ${open ? "" : "line-clamp-4 sm:line-clamp-6"}`}>
        {review.quote}
      </p>
      {(clamped || open) && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="self-start mt-1 text-sm font-semibold underline text-rosemary underline-offset-4 hover:no-underline"
        >
          {open ? "Read less" : "Read more"}
        </button>
      )}
      <p className="pt-3 mt-auto text-sm">
        <span className="font-bold text-castiron">{review.name}</span>
        {review.event && <span className="text-rosemary"> · {review.event}</span>}
      </p>
      <p className="mt-0.5 text-xs font-semibold text-rosemary/80">Verified Google review</p>
    </li>
  )
}

const Reviews = () => {
  const [showAll, setShowAll] = useState(false)
  const rowRef = useRef(null)
  const listRef = useRef(null)
  const showFewer = () => {
    setShowAll(false)
    const top = listRef.current?.getBoundingClientRect().top
    if (top !== undefined && top < 0) window.scrollTo({ top: window.scrollY + top - 96 })
  }
  const list = showAll ? REVIEWS : REVIEWS.slice(0, REVIEW_PREVIEW)
  const scrollRow = (dir) => {
    const row = rowRef.current
    if (row) row.scrollBy({ left: dir * row.clientWidth * 0.8, behavior: "smooth" })
  }

  return (
    <section id="reviews" className="py-10 scroll-mt-20 bg-rosemary md:py-14">
      <div className="flex flex-wrap items-end justify-between px-5 mx-auto mb-6 gap-x-6 gap-y-2 max-w-6xl">
        <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-linen font-display md:text-3xl">
          Don&apos;t take our word for it
        </h2>
        <div className="flex items-center gap-4">
          <p className="flex items-center gap-2 font-bold text-linen">
            <Stars className="w-4 h-4 text-linen" /> 100+ five-star Google reviews
          </p>
          <div className="hidden gap-2 md:flex">
            {[-1, 1].map((dir) => (
              <button
                key={dir}
                type="button"
                onClick={() => scrollRow(dir)}
                aria-label={dir < 0 ? "Previous photos" : "Next photos"}
                className="flex items-center justify-center w-10 h-10 transition-colors duration-200 border-2 rounded-full border-linen text-linen hover:bg-linen hover:text-rosemary"
              >
                <svg className={`w-4 h-4 ${dir < 0 ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Photo carousel: real events we catered */}
      <div ref={rowRef} className="flex gap-3 px-5 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-px-5 md:gap-4">
        {CAROUSEL.map((photo) => (
          <figure
            key={photo.src}
            className={`relative flex-shrink-0 h-[18.75rem] md:h-[23.75rem] ${photo.wide ? "aspect-[4/3]" : "aspect-[4/5]"} overflow-hidden rounded-2xl snap-start bg-sage`}
          >
            <img
              src={photo.src}
              alt={photo.alt}
              loading="lazy"
              decoding="async"
              className="object-cover w-full h-full"
            />
            <figcaption className="absolute inset-x-0 bottom-0 px-4 pt-12 pb-3 text-white bg-gradient-to-t from-black/80 via-black/40 to-transparent">
              <span className="block text-base font-bold leading-tight">{photo.event}</span>
              {photo.detail && <span className="block mt-0.5 text-sm text-white/85">{photo.detail}</span>}
            </figcaption>
          </figure>
        ))}
      </div>

      {/* Every verified Google review */}
      <div className="px-5 mx-auto mt-8 max-w-6xl">
        <ul ref={listRef} className="grid gap-2.5 sm:gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((review, i) => (
            <ReviewCard key={`${review.name}-${i}`} review={review} hideOnPhone={!showAll && i >= 4} />
          ))}
        </ul>

        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={showAll ? showFewer : () => setShowAll(true)}
            aria-expanded={showAll}
            className="px-6 py-3 font-bold transition-colors duration-200 border-2 rounded-full border-linen text-linen hover:bg-linen hover:text-rosemary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-linen"
          >
            {showAll ? "Show fewer reviews" : `Show all ${REVIEWS.length} reviews`}
          </button>
        </div>
      </div>
    </section>
  )
}

const Standards = () => {
  const standards = [
    "Extra virgin olive oil only. No seed oils.",
    "Made from scratch, even the bread and sauces.",
    "Nothing from a can but the tomatoes.",
    "Local and seasonal produce.",
    "Gluten-free cutlets and meatballs.",
    "Chicken raised without antibiotics.",
    "No dyes, fillers, preservatives or refined sugar.",
  ]

  return (
    <section className="px-5 py-8 bg-white md:py-12">
      <div className="flex items-start gap-6 mx-auto max-w-7xl md:gap-10 lg:gap-14">
        <img
          src="/cik-logo-dark.png"
          alt="Cast Iron Kitchen, est. 2023"
          width="394"
          height="640"
          className="flex-shrink-0 hidden w-auto md:block md:h-64 lg:h-72"
        />
        <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight font-display md:text-4xl">
            Why we&apos;re different
          </h2>
          <img src="/cik-logo-dark.png" alt="" width="394" height="640" className="flex-shrink-0 w-auto h-28 md:hidden" />
        </div>
        <ul className="grid gap-x-8 gap-y-4 mt-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-6">
          {standards.map((standard) => (
            <li key={standard} className="flex gap-3">
              <span className="flex items-center justify-center flex-shrink-0 w-7 h-7 rounded-lg bg-sage/60">
                <Check className="!w-4 !h-4 text-rosemary" />
              </span>
              <span className="text-lg font-semibold leading-snug text-castiron">{standard}</span>
            </li>
          ))}
        </ul>
        </div>
      </div>
    </section>
  )
}

const MeetDylan = () => (
  <section id="dylan" className="px-5 py-10 bg-linen md:py-14">
    <div className="grid items-center gap-8 mx-auto max-w-6xl md:grid-cols-2 md:gap-12">
      <video
        className="w-full bg-castiron rounded-2xl aspect-video"
        controls
        playsInline
        preload="none"
        poster="/chef-dylan.webp"
      >
        <source src="/cik-vsl.mp4" type="video/mp4" />
        Your browser cannot play this video.
      </video>

      <div>
        <h2 className="text-2xl font-extrabold leading-tight tracking-tight font-display md:text-3xl">
          Know exactly who&apos;s cooking for your guests
        </h2>
        {/* Dylan's story, in his words. */}
        <div className="mt-4 space-y-4 text-lg leading-relaxed max-w-[60ch] text-castiron">
          <p>
            I grew up watching my mom and grandma cook simple Sicilian dishes. After my mom passed away, I put
            everything into food, fitness and nutrition.
          </p>
          <p>
            Cast Iron Kitchen is how I pass her recipes on, held to a higher standard. Too much of today&apos;s food is
            full of things that don&apos;t belong there. I believe food can be indulgent and still be good for you, so
            we choose every ingredient with care and never cut corners.
          </p>
          <p>Your guests will love every bite. They&apos;ll never guess it&apos;s good for them.</p>
        </div>
        <p className="mt-4 font-bold text-castiron">Dylan Aquila, chef &amp; owner</p>
      </div>
    </div>
  </section>
)

// Real limits only: 4 events a weekend, and weekends fill first.
const Dates = () => (
  <section className="px-5 py-8 bg-castiron md:py-10">
    <div className="flex flex-col gap-5 mx-auto max-w-6xl md:flex-row md:items-center md:justify-between">
      <div>
        <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-linen font-display md:text-3xl">
          Don&apos;t lose your date. We cater only 4 events each weekend.
        </h2>
        <p className="mt-2 text-lg text-linen/85">
          Weekends fill first. Book weddings about 6 months ahead and other events at least a month ahead.
        </p>
      </div>
      <Cta place="dates" className="w-full md:w-auto md:flex-shrink-0" />
    </div>
  </section>
)

// The Typeform, created directly so it also works under React's development double-mount.
const QuoteForm = ({ onEvent }) => {
  const ref = useRef(null)
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  useEffect(() => {
    if (!ref.current) return undefined
    // Each mount gets its own holder; removing it discards the form even if it attaches late.
    const holder = document.createElement("div")
    ref.current.appendChild(holder)
    const widget = createWidget(FORM_ID, {
      container: holder,
      hidden: quoteHiddenFields(),
      inlineOnMobile: true,
      autoResize: "740,1200",
      noScrollbars: true,
      onReady: () => onEventRef.current("quote_form_loaded"),
      onStarted: () => onEventRef.current("quote_form_started"),
      onSubmit: () => onEventRef.current("quote_form_submitted"),
    })
    return () => {
      widget.unmount()
      holder.remove()
    }
  }, [])

  return <div ref={ref} className="w-full min-h-[740px]" />
}

// The quote form, built into the page. Every button on the site scrolls here.
const Book = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])
  const track = (event) => {
    try {
      window.posthog?.capture(event)
    } catch (e) {}
  }

  return (
    <section id="book" className="px-4 py-10 scroll-mt-16 bg-sage sm:px-5 md:py-14">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-center font-display md:text-3xl">
          Fill out the form
        </h2>
        <p className="mt-2 text-lg text-center text-castiron">
          It&apos;s short, and you&apos;ll see starting prices as you go.
        </p>
        <ul className="grid max-w-xl gap-x-6 gap-y-2 mx-auto mt-4 sm:grid-cols-2">
          {[
            "Your all-in quote within 24 hours",
            "One price, with no hidden fees",
            "Price and date locked with your deposit",
            "We\u2019ve never run out of food, and we never will",
          ].map((promise) => (
            <li key={promise} className="flex gap-2 font-semibold text-castiron">
              <Check className="mt-0.5 text-rosemary" />
              {promise}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-center text-castiron">
          Want the menu open while you choose?{" "}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="font-semibold underline underline-offset-4 hover:no-underline"
          >
            View the menu
          </button>
        </p>

        <div className="mt-6 overflow-hidden bg-white shadow-lg rounded-2xl">
          <QuoteForm onEvent={track} />
        </div>
      </div>

      {menuOpen && <Lightbox page={MENU_PAGES[0]} onClose={closeMenu} />}
    </section>
  )
}

const FAQS = [
  {
    q: "How much does catering cost?",
    a: "You'll see starting prices right in the form. Your exact quote depends on your guest count, menu and service style. It's all-in: one price, with no hidden fees.",
  },
  {
    q: "How fast will I hear back?",
    a: "Within 24 hours of sending the form, you'll get a call with your all-in quote.",
  },
  {
    q: "What if we run out of food?",
    a: "You won't. We've never run out of food at an event. We plan enough for every guest, and with Served Buffet and Family Style, our servers keep it coming throughout your event. Nobody goes hungry, guaranteed.",
  },
  {
    q: "Will the food arrive hot and on time?",
    a: "Yes. Every order is delivered hot and set up by our team.",
  },
  {
    q: "Will I be stuck working my own party?",
    a: "Not with Served Buffet or Family Style. Our team serves and cleans up, so you can be with your guests.",
  },
  {
    q: "How far ahead should I book?",
    a: "As early as you can. We cater only 4 events each weekend, and weekends fill first. Weddings typically book about 6 months ahead. For other events, plan on at least a month. Need it sooner? We'll make it work when our calendar allows, with a last-minute fee.",
  },
  {
    q: "Is there a guest minimum?",
    a: "Yes. Hot Drop-Off starts at 25 guests, Served Buffet at 50 and Family Style at 100.",
  },
  {
    q: "Do you offer tastings?",
    a: "Yes, for any event. Tastings are paid, and the cost is credited to your bill when you book.",
  },
  {
    q: "Can you accommodate food allergies?",
    a: "Yes. Every dish on our menu is labeled for gluten, milk, egg, wheat, mustard and tree nuts, and our cutlets and meatballs are gluten-free, so no one is stuck with a side salad. Our kitchen does handle wheat, milk, egg and tree nuts, so tell us about any serious allergies on the form and we'll walk you through how we prepare those dishes.",
  },
  {
    q: "What's included?",
    a: "Every menu includes bread and our house sauces. Every event is made from scratch, delivered hot and set up by our team. Served Buffet and Family Style also include servers, chafers, cleanup and leftovers packed for you. Hot Drop-Off arrives in pans, and you can add disposable chafers and serving utensils.",
  },
  {
    q: "What areas do you serve?",
    a: "We serve events within about 35 miles of Willowick, Ohio. Beyond that, a travel fee applies based on distance.",
  },
  {
    q: "When are my price and date locked in?",
    a: "As soon as you pay your deposit. After that, your price and date won't change.",
  },
  {
    q: "Can you help me choose the menu?",
    a: "Of course. Our team can walk you through the options and recommend what works best for your guests.",
  },
]

const Faq = () => (
  <section className="px-5 pt-2 pb-12 bg-linen md:pb-16">
    <div className="mx-auto max-w-3xl">
      <h2 className="text-2xl font-extrabold leading-tight tracking-tight font-display md:text-3xl">
        Questions hosts ask before they book
      </h2>
      <div className="mt-5 border-t border-sage">
        {FAQS.map((item) => (
          <details key={item.q} className="py-4 border-b group border-sage">
            <summary className="flex items-center justify-between gap-4 text-lg font-bold cursor-pointer list-none text-castiron [&::-webkit-details-marker]:hidden">
              {item.q}
              <svg className="flex-shrink-0 w-5 h-5 transition-transform duration-200 text-rosemary group-open:rotate-45" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
              </svg>
            </summary>
            <p className="mt-3 leading-relaxed text-castiron">{item.a}</p>
          </details>
        ))}
      </div>
      <div className="flex justify-center mt-8">
        <Cta place="faq" className="w-full sm:w-auto" />
      </div>
    </div>
  </section>
)

const Footer = () => (
  <footer className="px-5 py-10 bg-castiron">
    <div className="grid gap-8 mx-auto max-w-6xl sm:grid-cols-2">
      <div className="flex items-center gap-5">
        <img src="/cik-logo-light.png" alt="" width="394" height="640" loading="lazy" className="flex-shrink-0 w-auto h-32 md:h-40" />
        <div>
          <p className="text-xl font-bold text-linen font-display">Cast Iron Kitchen</p>
          <p className="mt-2 text-sm text-linen/60">Sicilian &amp; Italian catering in Willowick, Ohio</p>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:items-end">
        <a href="mailto:castironkitchenwillowick@gmail.com" className="break-all text-linen/80 hover:text-sage">
          castironkitchenwillowick@gmail.com
        </a>
        <a href={MEAL_PREP_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-linen/60 hover:text-sage">
          Meal prep
        </a>
        <a href={FAMILY_PANS_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-linen/60 hover:text-sage">
          Family dinner
        </a>
        <a href="/privacy.html" className="text-sm text-linen/60 hover:text-sage">
          Privacy policy
        </a>
      </div>
    </div>

    <p className="pt-8 mx-auto mt-8 text-sm border-t max-w-6xl border-linen/15 text-linen/50">
      &copy; {new Date().getFullYear()} Cast Iron Kitchen
    </p>
  </footer>
)

const CateringPage = () => (
  <>
    <Nav />
    <main>
      <Hero />
      <Standards />
      <Menu />
      <Reviews />
      <MeetDylan />
      <Dates />
      <Book />
      <Faq />
    </main>
    <Footer />
  </>
)

export default CateringPage
